import React, { useState, useEffect, useMemo, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { User } from "@terveystalo/web-auth-onewelcome"

import { Button, Title, Lead, TextField } from "@terveystalo/design-system-react-components"
import { Autocomplete } from "./components/Autocomplete"
import { PrintableFormView } from "./components/PrintableFormView"
import authClient from "./UserManager"
import { getBackend, ToimipaikkaData as LocationData } from "./client"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"

const client = getBackend()

interface CorrectionRequestFormProps {
  user: User | null
  isLoggedIn: boolean
}

interface CorrectionRequestFormState {
  location?: string
  requestTypes: string[] // Multiple selections: removal, modification, addition, purchaseServiceCustomer
  descriptionRemoval?: string
  descriptionModification?: string
  descriptionAddition?: string
  descriptionPurchaseServiceCustomer?: string
  address?: string
  phoneNumber?: string
  email?: string
}

const korjauspyyntoFormInitialState: CorrectionRequestFormState = {
  location: undefined,
  requestTypes: [],
  descriptionRemoval: "",
  descriptionModification: "",
  descriptionAddition: "",
  descriptionPurchaseServiceCustomer: "",
  address: "",
  phoneNumber: "",
  email: "",
}

function CorrectionRequestForm({ user, isLoggedIn }: CorrectionRequestFormProps) {
  const { t, i18n } = useTranslation()
  const routeLocation = useLocation()
  const navigate = useNavigate()

  // Detect print mode from URL query parameter
  const searchParams = new URLSearchParams(routeLocation.search)
  const isPrintMode = searchParams.get("mode") === "print"

  const { formState, setFormState, saveFormState, isRestoring } = useFormPersistence({
    formKey: "korjauspyyntoForm",
    initialState: korjauspyyntoFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [locationList, setLocationList] = useState<LocationData[]>([])
  const [files, setFiles] = useState<File[]>([])
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Update page title when language changes
  React.useEffect(() => {
    document.title = t("pageTitleKorjauspyynto")
  }, [i18n.language, t])

  // Pre-fill email from user profile
  React.useEffect(() => {
    if (user?.profile?.email && !formState.email) {
      setFormState((prev) => ({ ...prev, email: user.profile.email }))
    }
  }, [user, formState.email, setFormState])

  // Disable scrolling when submit message modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  // Fetch location list on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await client.getToimipaikka()
        if (data.data && data.data.length > 0) {
          setLocationList(data.data)
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error)
      }
    }
    fetchLocations()
  }, [])

  // Create location options from fetched list
  const locationOptions = useMemo(() => {
    return [
      { label: t("korjauspyyntoForm.toimipaikkaSelect"), value: "" },
      ...locationList.map((loc) => {
        const translationKey = `toimipaikkaNames.${loc.name}` as const
        const translatedName = t(translationKey, { defaultValue: loc.name })
        return {
          label: translatedName,
          value: loc.id,
        }
      }),
    ]
  }, [locationList, t])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleCheckboxChange = (requestType: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    setFormState((prevState) => {
      const newRequestTypes = isChecked
        ? [...prevState.requestTypes, requestType]
        : prevState.requestTypes.filter((type) => type !== requestType)
      return {
        ...prevState,
        requestTypes: newRequestTypes,
      }
    })
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (formState.requestTypes.length === 0) {
      setSubmitMessage(t("error.requestTypeRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.location) {
      setSubmitMessage(t("error.toimipaikkaRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.email) {
      setSubmitMessage(t("error.emailRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      // Check if we have files to upload
      if (files.length > 0) {
        // Use FormData for multipart/form-data upload
        const formData = new FormData()

        // Append form fields
        formData.append("location", formState.location!)
        // Append array items individually (TSOA/multer expects this format)
        formState.requestTypes.forEach((type) => {
          formData.append("requestTypes", type)
        })
        if (formState.descriptionRemoval)
          formData.append("descriptionRemoval", formState.descriptionRemoval)
        if (formState.descriptionModification)
          formData.append("descriptionModification", formState.descriptionModification)
        if (formState.descriptionAddition)
          formData.append("descriptionAddition", formState.descriptionAddition)
        if (formState.descriptionPurchaseServiceCustomer)
          formData.append(
            "descriptionPurchaseServiceCustomer",
            formState.descriptionPurchaseServiceCustomer,
          )
        if (formState.address) formData.append("address", formState.address)
        if (formState.phoneNumber) formData.append("phoneNumber", formState.phoneNumber)
        formData.append("email", formState.email!)

        // Append files
        files.forEach((file) => {
          formData.append("files", file)
        })

        // Make direct API call with FormData
        const backendUrl = config.backendHost
        const response = await fetch(`${backendUrl}/api/korjauspyynto`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${await authClient.getUser().then((u) => u?.access_token)}`,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
      } else {
        // No files - use regular JSON submission
        const backendUrl = config.backendHost
        const response = await fetch(`${backendUrl}/api/korjauspyynto`, {
          method: "POST",
          body: JSON.stringify(formState),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await authClient.getUser().then((u) => u?.access_token)}`,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
      }

      setSubmitMessage(t("success.korjauspyyntoSubmitted"))
      // Reset form
      setFormState({
        ...korjauspyyntoFormInitialState,
        email: user?.profile?.email || "",
      })
      setFiles([])
    } catch (error) {
      console.error(error)
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const login = async () => {
    // Store current location before redirecting to authentication
    console.log(
      "[CorrectionRequestForm] Authenticate clicked - Current location:",
      routeLocation.pathname,
    )
    // Save form state before redirect
    await saveFormState()
    sessionStorage.setItem("returnUrl", routeLocation.pathname)
    console.log(
      "[CorrectionRequestForm] Stored returnUrl in sessionStorage:",
      routeLocation.pathname,
    )

    try {
      console.log("[CorrectionRequestForm] Calling authClient.signinRedirect()...")
      await authClient.signinRedirect()
      console.log("[CorrectionRequestForm] signinRedirect completed (should have redirected)")
    } catch (error) {
      console.error("[CorrectionRequestForm] Error during signinRedirect:", error)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  // Show login prompt if not authenticated and not in print mode
  if (!isLoggedIn && !isPrintMode) {
    return (
      <div className="min-h-screen bg-coal04">
        <div className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("korjauspyyntoForm.title")}</Title>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-4 mt-4"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              {/* Authenticated option */}
              <div className="mb-4 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto mb-2 h-5 w-5 text-coal65"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
                <p className="mb-2 font-bold">{t("korjauspyyntoForm.authenticatedExplanation")}</p>
                <p className="mb-3 text-sm">{t("korjauspyyntoForm.notAuthenticatedMessage")}</p>
                <Button onClick={login}>{t("login")}</Button>
              </div>

              {/* Divider */}
              <div className="my-4 border-t border-gray-300"></div>

              {/* Print mode option */}
              <div className="text-center">
                <p className="mb-3 text-sm">{t("korjauspyyntoForm.printModeExplanation")}</p>
                <Button
                  onClick={() => navigate(routeLocation.pathname + "?mode=print")}
                  variant="secondary"
                >
                  {t("korjauspyyntoForm.usePrintableForm")}
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04">
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15 no-print">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("korjauspyyntoForm.title")}</Title>
              <Lead>
                <Trans
                  i18nKey="korjauspyyntoForm.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className="s:px-6 m:px-8 no-print">
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Title as="h2" size="l" className="mb-2">
                {t("korjauspyyntoForm.formInfo")}
              </Title>

              <p className="text-coal85 mb-2 px-2 py-0.5 bg-blue-50 rounded border border-blue-200">
                {t("korjauspyyntoForm.gdprNotice")}
              </p>

              {/* Print mode banner */}
              {isPrintMode && (
                <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-400 no-print">
                  <p className="text-sm font-bold text-yellow-900">
                    {t("korjauspyyntoForm.printModeBanner")}
                  </p>
                  <p className="text-sm text-yellow-800 mt-1">
                    {t("korjauspyyntoForm.printInstructions")}
                  </p>
                </div>
              )}

              <div className="space-y-2 mb-4">
                {/* Location */}
                <div className="space-y-2 mb-4">
                  <Autocomplete
                    id="location"
                    name="location"
                    label={t("korjauspyyntoForm.toimipaikkaLabel")}
                    placeholder={t("korjauspyyntoForm.toimipaikkaPlaceholder")}
                    options={locationOptions}
                    value={formState.location || ""}
                    onChange={(value) => setFormState({ ...formState, location: value })}
                    required
                  />
                </div>

                {/* Request Types (Checkboxes) */}
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("korjauspyyntoForm.requestTypesLabel")}{" "}
                    <span style={{ color: "#CE0F69" }}>*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="requestType"
                        value="removal"
                        checked={formState.requestTypes.includes("removal")}
                        onChange={handleCheckboxChange("removal")}
                        className="mr-2"
                      />
                      {t("korjauspyyntoForm.requestTypePoistettaviksi")}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="requestType"
                        value="modification"
                        checked={formState.requestTypes.includes("modification")}
                        onChange={handleCheckboxChange("modification")}
                        className="mr-2"
                      />
                      {t("korjauspyyntoForm.requestTypeMuutettaviksi")}
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="requestType"
                        value="addition"
                        checked={formState.requestTypes.includes("addition")}
                        onChange={handleCheckboxChange("addition")}
                        className="mr-2"
                      />
                      {t("korjauspyyntoForm.requestTypeLisattaviksi")}
                    </label>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="requestType"
                        value="purchaseServiceCustomer"
                        checked={formState.requestTypes.includes("purchaseServiceCustomer")}
                        onChange={handleCheckboxChange("purchaseServiceCustomer")}
                        className="mr-2 mt-1 flex-shrink-0"
                      />
                      <span>
                        <Trans
                          i18nKey="korjauspyyntoForm.requestTypeOstopalveluasiakkaana"
                          components={{
                            i: <i />,
                          }}
                        />
                      </span>
                    </label>
                  </div>
                </div>

                {/* Conditional description fields based on selected request types */}
                {formState.requestTypes.includes("removal") && (
                  <TextField
                    id="descriptionRemoval"
                    name="descriptionRemoval"
                    label={t("korjauspyyntoForm.descriptionPoistettaviksiLabel")}
                    as="textarea"
                    value={formState.descriptionRemoval}
                    onChange={handleInputChange}
                    rows={3}
                  />
                )}

                {formState.requestTypes.includes("modification") && (
                  <TextField
                    id="descriptionModification"
                    name="descriptionModification"
                    label={t("korjauspyyntoForm.descriptionMuutettaviksiLabel")}
                    as="textarea"
                    value={formState.descriptionModification}
                    onChange={handleInputChange}
                    rows={3}
                  />
                )}

                {formState.requestTypes.includes("addition") && (
                  <TextField
                    id="descriptionAddition"
                    name="descriptionAddition"
                    label={t("korjauspyyntoForm.descriptionLisattaviksiLabel")}
                    as="textarea"
                    value={formState.descriptionAddition}
                    onChange={handleInputChange}
                    rows={3}
                  />
                )}

                {formState.requestTypes.includes("purchaseServiceCustomer") && (
                  <TextField
                    id="descriptionPurchaseServiceCustomer"
                    name="descriptionPurchaseServiceCustomer"
                    label={t("korjauspyyntoForm.descriptionOstopalveluasiakkaanaLabel")}
                    as="textarea"
                    value={formState.descriptionPurchaseServiceCustomer}
                    onChange={handleInputChange}
                    rows={3}
                  />
                )}

                {/* Contact information */}
                <TextField
                  id="address"
                  name="address"
                  label={t("korjauspyyntoForm.addressLabel")}
                  as="textarea"
                  value={formState.address}
                  onChange={handleInputChange}
                  rows={2}
                  required={false}
                />

                <TextField
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  label={t("korjauspyyntoForm.phoneNumberLabel")}
                  value={formState.phoneNumber}
                  onChange={handleInputChange}
                  required={false}
                />

                <TextField
                  id="email"
                  name="email"
                  type="email"
                  label={t("korjauspyyntoForm.emailLabel")}
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                />

                {/* File upload - hidden in print mode */}
                {!isPrintMode && (
                  <div className="space-y-2 mb-4">
                    <label
                      htmlFor="file-upload-korjauspyynto"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("form.attachments")}
                    </label>
                    <input
                      id="file-upload-korjauspyynto"
                      name="files"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    {files.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {t("form.filesSelected", { count: files.length })}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{t("form.fileUploadHint")}</p>
                  </div>
                )}
              </div>

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Show print button in print mode, submit button otherwise */}
              {isPrintMode ? (
                <Button type="button" onClick={() => window.print()} className="no-print">
                  {t("korjauspyyntoForm.printButton")}
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t("form.submitting") : t("submit")}
                </Button>
              )}
            </div>
          </main>

          {/* Printable form view - hidden on screen, visible when printing */}
          {isPrintMode && (
            <div className="hidden print:block">
              <PrintableFormView formState={formState} locationList={locationList} />
            </div>
          )}
        </div>

        {/* Success/Error Modal */}
        {submitMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "32px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: "500px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
              <Button
                type="button"
                onClick={() => (window.location.href = "https://www.terveystalo.com")}
              >
                {t("close")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </form>
  )
}

export default CorrectionRequestForm
